import { useEffect } from 'react';
import { QueryKey, useQuery } from '@tanstack/react-query';

import { useApiClient } from 'app/data/api-client';
import { GetThemeQueryResponse, Theme } from 'app/data/types/theme';
import { ERROR_DEFAULT } from 'app/shared/consts/error-codes';
import { useBuilding } from 'app/shared/contexts/building';
import { useErrors } from 'app/shared/contexts/errors';
import { retryOnAuthError } from 'app/shared/auth';

export const THEME_QUERY_KEY = 'theme';

export const getUseThemeQueryKey = (buildingUuid: string): QueryKey => [THEME_QUERY_KEY, buildingUuid];

/**
 * Fetches the user's building theme. Skipped when `sdkTheme` is already
 * provided by the host frame's initResponse — that's the common path.
 */
export const useThemeQuery = (sdkTheme?: Theme | null): GetThemeQueryResponse => {
  const client = useApiClient();
  const { addGenericError, hideGenericErrors } = useErrors();
  const { building } = useBuilding();
  const buildingUuid = building?.uuid;

  const { data, error, isLoading, isSuccess } = useQuery<Theme | null, Error>({
    queryKey: getUseThemeQueryKey(buildingUuid ?? ''),
    queryFn: async () => {
      const theme = await client.get<Theme>(`/api/buildings/${buildingUuid}/theme`);
      return theme ?? null;
    },
    enabled: !!buildingUuid && !sdkTheme,
    retry: retryOnAuthError,
  });

  useEffect(() => {
    if (error) {
      addGenericError(THEME_QUERY_KEY, { error, errorCode: ERROR_DEFAULT });
    } else {
      hideGenericErrors(THEME_QUERY_KEY);
    }
  }, [error, addGenericError, hideGenericErrors]);

  return {
    theme: sdkTheme ?? data ?? null,
    error: error ? { error, errorCode: ERROR_DEFAULT } : null,
    isLoading,
    isSuccess,
  };
};
